import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { 
  Facebook, 
  Twitter, 
  Linkedin, 
  Instagram, 
  Youtube,
  Mail,
  MapPin,
  CreditCard,
  Smartphone
} from "lucide-react";
import tourbiLogo from "@/assets/tourbi-logo.png";
import flutterwaveLogo from "@/assets/flutterwave-logo.svg";

const footerLinks = {
  products: [
    "Travel CRM",
    "Booking Management",
    "Inventory System",
    "AI Itinerary Builder",
    "HR & People Management",
    "Account Profit & Loss",
    "AI BI Dashboard Report",
    "Marketing",
    "Operations",
    "Custom Branding",
    "Payment Hub",
    "Channel Manager",
  ],
  solutions: [
    "For Tour Operators",
    "For Travel Agencies",
    "For DMCs",
    "For Activity Providers",
    "Enterprise",
  ],
  resources: [
    { label: "Blog", href: "/blog" },
    { label: "Case Studies", href: "#" },
    { label: "Webinars", href: "#" },
    { label: "Pricing", href: "/pricing" },
    { label: "API Docs", href: "/api-docs" },
    { label: "Help Center", href: "/help-center" },
    { label: "Community", href: "/community" },
  ],
  company: [
    { label: "About Us", href: "/about" },
    { label: "Careers", href: "/careers" },
    { label: "Partners", href: "/partners" },
    { label: "Press", href: "/press" },
    { label: "Contact", href: "/contact" },
  ],
};

const socialLinks = [
  { icon: Facebook, href: "https://www.facebook.com/profile.php?id=61586979932712" },
  { icon: Instagram, href: "https://www.instagram.com/tourbicrm/" },
];

const paymentMethods = [
  { name: "Flutterwave", icon: "🦋" },
  { name: "Mastercard", icon: null, isMastercard: true },
  { name: "Visa", icon: null, isVisa: true },
  { name: "MomoPay", icon: null, isMomo: true },
];

const handleProductClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
  e.preventDefault();
  const productsSection = document.querySelector('#products');
  if (productsSection) {
    productsSection.scrollIntoView({ behavior: 'smooth' });
  } else {
    window.location.href = '/#products';
  }
};

const handleSolutionsClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
  e.preventDefault();
  const solutionsSection = document.querySelector('#solutions');
  if (solutionsSection) {
    solutionsSection.scrollIntoView({ behavior: 'smooth' });
  } else {
    window.location.href = '/#solutions';
  }
};

export const Footer = () => {
  return (
    <footer className="bg-foreground text-background">
      <div className="section-container py-16 lg:py-20">
        {/* Main Footer Content */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 lg:gap-12">
          {/* Brand Column */}
          <div className="col-span-2 lg:col-span-2">
            <a href="/" className="flex items-center gap-2 mb-6" aria-label="Tourbi - Go to homepage">
              <img src={tourbiLogo} alt="Tourbi logo" width={120} height={40} className="h-10 w-auto" />
              <span className="text-xl font-display font-bold">Tourbi</span>
            </a>
            <p className="text-background/70 mb-6 max-w-xs">
              The complete travel technology platform for modern tour operators and travel agencies.
            </p>
            <div className="space-y-3 text-sm text-background/70">
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4" />
                <a href="mailto:hello@toubi.tech" className="hover:text-background transition-colors">
                  hello@toubi.tech
                </a>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                <span>KG 171 Kigali Rwanda</span>
              </div>
            </div>
          </div>

          {/* Products */}
          <div>
            <h3 className="font-display font-semibold mb-4 text-base">Products</h3>
            <ul className="space-y-3">
              {footerLinks.products.map((link) => (
                <li key={link}>
                  <a 
                    href="#products" 
                    onClick={handleProductClick}
                    className="text-sm text-background/70 hover:text-background transition-colors cursor-pointer"
                  >
                    {link}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Solutions */}
          <div>
            <h3 className="font-display font-semibold mb-4 text-base">Solutions</h3>
            <ul className="space-y-3">
              {footerLinks.solutions.map((link) => (
                <li key={link}>
                  <a 
                    href="#solutions" 
                    onClick={handleSolutionsClick}
                    className="text-sm text-background/70 hover:text-background transition-colors cursor-pointer"
                  >
                    {link}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h3 className="font-display font-semibold mb-4 text-base">Resources</h3>
            <ul className="space-y-3">
              {footerLinks.resources.map((link) => (
                <li key={link.label}>
                  <a 
                    href={link.href} 
                    target={link.href.startsWith("http") ? "_blank" : undefined}
                    rel={link.href.startsWith("http") ? "noopener noreferrer" : undefined}
                    className="text-sm text-background/70 hover:text-background transition-colors"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h3 className="font-display font-semibold mb-4 text-base">Company</h3>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.label}>
                  <a 
                    href={link.href} 
                    className="text-sm text-background/70 hover:text-background transition-colors"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Payment Methods Section */}
        <div className="mt-12 pt-8 border-t border-background/10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-center md:text-left">
              <h3 className="font-display font-semibold mb-2 text-base">Accepted Payment Methods</h3>
              <p className="text-sm text-background/75">Secure payments powered by Flutterwave</p>
            </div>
            <div className="flex items-center gap-4 flex-wrap justify-center">
              {/* Flutterwave */}
              <div className="flex items-center gap-2 bg-background/10 px-4 py-2 rounded-lg">
                <img src={flutterwaveLogo} alt="Flutterwave" width={80} height={20} className="h-5 w-auto" />
              </div>
              {/* Mastercard */}
              <div className="flex items-center gap-2 bg-background/10 px-4 py-2 rounded-lg">
                <div className="flex items-center">
                  <div className="w-5 h-5 bg-red-500 rounded-full -mr-2"></div>
                  <div className="w-5 h-5 bg-yellow-500 rounded-full opacity-80"></div>
                </div>
                <span className="text-sm font-medium ml-1">Mastercard</span>
              </div>
              {/* Visa */}
              <div className="flex items-center gap-2 bg-background/10 px-4 py-2 rounded-lg">
                <CreditCard className="h-5 w-5 text-blue-400" />
                <span className="text-sm font-medium">Visa</span>
              </div>
              {/* MomoPay */}
              <div className="flex items-center gap-2 bg-background/10 px-4 py-2 rounded-lg">
                <Smartphone className="h-5 w-5 text-yellow-400" />
                <span className="text-sm font-medium">MomoPay</span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-8 pt-8 border-t border-background/10 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="text-sm text-background/75">
            © {new Date().getFullYear()} Tourbi. All rights reserved.
          </div>
          
          <div className="flex items-center gap-4">
            <Link to="/privacy-policy" className="text-sm text-background/75 hover:text-background transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms-of-service" className="text-sm text-background/75 hover:text-background transition-colors">
              Terms of Service
            </Link>
            <Link to="/cookie-policy" className="text-sm text-background/75 hover:text-background transition-colors">
              Cookie Policy
            </Link>
          </div>

          <div className="flex items-center gap-4">
            {socialLinks.map((social, index) => (
              <a
                key={index}
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-background/75 hover:text-background transition-colors"
                aria-label={`Visit Tourbi on ${social.href.includes('linkedin') ? 'LinkedIn' : social.href.includes('youtube') ? 'YouTube' : 'social media'}`}
              >
                <social.icon className="h-5 w-5" />
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};
